<template>
  <section class="login-page">
    <loading :active="isLoading" :is-full-page="true"></loading>
    <main class="login-shell">
      <article class="login-card">
        <header class="login-heading">
          <div class="login-logo-wrap">
            <img src="@/assets/doflogo.png" alt="Dansk Ornitologisk Forening" />
          </div>
          <span class="login-kicker">DOFbasen</span>
          <h1>Spontantællinger</h1>
          <p>
            {{
              $englishPreferred
                ? "Sign in with your DOFbasen observer code."
                : "Log ind med din obserkode fra DOFbasen."
            }}
          </p>
          <a
            class="login-read-more"
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.dof.dk/fakta-om-fugle/punkttaellingsprogrammet"
          >
            {{
              $englishPreferred
                ? "Read about spontaneous counts"
                : "Læs om spontantællinger"
            }}
            <i class="material-icons" aria-hidden="true">open_in_new</i>
          </a>
        </header>

        <PWAInstallPromp class="login-install-prompt" />

        <div v-if="showAlert" id="login-error" class="login-alert" role="alert">
          <i class="material-icons" aria-hidden="true">error_outline</i>
          <span>
            {{
              $englishPreferred
                ? "The observer code and password did not match."
                : "Kombinationen af obserkode og adgangskode kunne ikke findes."
            }}
          </span>
          <button
            type="button"
            @click="toggleShowAlert()"
            :aria-label="$englishPreferred ? 'Close message' : 'Luk besked'"
          >
            &times;
          </button>
        </div>

        <form class="login-form" @submit.prevent="attemptLogin">
          <label for="typeUsername">
            {{ $englishPreferred ? "Observer code" : "Obserkode" }}
          </label>
          <div class="login-input-wrap">
            <i class="material-icons" aria-hidden="true">person</i>
            <input
              v-model="username"
              autocomplete="username"
              type="text"
              id="typeUsername"
              :placeholder="$englishPreferred ? 'Observer code' : 'Obserkode'"
              :aria-invalid="showAlert"
              :aria-describedby="showAlert ? 'login-error' : undefined"
            />
          </div>

          <label for="typePasswordX">
            {{ $englishPreferred ? "Password" : "Kodeord" }}
          </label>
          <div class="login-input-wrap">
            <i class="material-icons" aria-hidden="true">lock</i>
            <input
              v-model="password"
              autocomplete="current-password"
              :placeholder="$englishPreferred ? 'Password' : 'Kodeord'"
              type="password"
              id="typePasswordX"
              :aria-invalid="showAlert"
              :aria-describedby="showAlert ? 'login-error' : undefined"
            />
          </div>

          <a
            target="_blank"
            rel="noopener noreferrer"
            class="login-forgot-link"
            href="https://dofbasen.dk/reminder.php"
          >
            {{ $englishPreferred ? "Forgot your password?" : "Glemt kodeord?" }}
          </a>

          <button
            class="login-submit-button"
            :disabled="isLoginCredentialsEmpty"
            type="submit"
          >
            <span>{{ $englishPreferred ? "Sign in" : "Log ind" }}</span>
            <i class="material-icons" aria-hidden="true">arrow_forward</i>
          </button>
        </form>
      </article>
    </main>
  </section>
</template>

<script>
import ageRepository from "../idb/repositories/ageRepository";
import siteRepository from "../idb/repositories/siteRepository";
import speciesRepository from "../idb/repositories/specieRepository";
import plumageRepository from "../idb/repositories/plumageRepository";
import primaryBehaviourRepository from "../idb/repositories/primaryBehaviourRepository";
import secondaryBehaviourRepository from "../idb/repositories/secondaryBehaviourRepository";
import directionRepository from "../idb/repositories/directionRepository";
import atlasSquareRepository from "../idb/repositories/atlasSquareRepository";
import atlasSquarePointRepository from "../idb/repositories/atlasSquarePointRepository";
import generalAtlasSquareStatistics from "../idb/repositories/generalAtlasSquareStatisticRepository";
import speciesChecksumRepository from "../idb/repositories/speciesChecksumRepository";
import sitesChecksumRepository from "../idb/repositories/sitesChecksumRepository";
import specieRankingRepository from "../idb/repositories/SpecieRankingRepository";
import atlas32Squares from "@/assets/json/atlas_squares/atlas_squares_utm_32.json";
import atlas33Squares from "@/assets/json/atlas_squares/atlas_squares_utm_33.json";
import finnSiteRepository from "../idb/repositories/finnSiteRepository";
import finnPointRepository from "../idb/repositories/finnPointRepository";
import finnPointsChecksumRepository from "../idb/repositories/finnPointsChecksumRepository";
import PWAInstallPromp from "@/components/PWAInstallPrompt";
import axios from "axios";
import synchronizationService from "../synchronization/synchronizationService";
import localforage from "localforage";
// Import component
import Loading from "vue3-loading-overlay";
// Import stylesheet
import "vue3-loading-overlay/dist/vue3-loading-overlay.css";
const alarmSound = require("../assets/audio/level-completed.mp3");
const offlineMapImage = require("../assets/offline-denmark.png");

export default {
  name: "Login",
  components: {
    PWAInstallPromp,
    localforage,
    Loading,
  },
  data() {
    return {
      username: null,
      password: null,
      showAlert: false,
      alarmSound,
      offlineMapImage,
      isLoading: false,
    };
  },
  mounted() {
    console.time("preparing data");
    // Get data necessary for app to work properly
    atlasSquareRepository.insertAtlasSquares(atlas32Squares);
    atlasSquareRepository.insertAtlasSquares(atlas33Squares);

    console.timeEnd("preparing data");
  },
  methods: {
    async getData() {
      console.log("getData()");
      await this.getPrimaryBehaviours().then((response) => {
        primaryBehaviourRepository.insertPrimaryBehaviours(response.data);
      });
      await this.getSecondaryBehaviours().then((response) => {
        secondaryBehaviourRepository.insertSecondaryBehaviours(response.data);
      });
      await this.getDirections().then((response) => {
        directionRepository.insertDirections(response.data);
      });
      await this.getAges().then((response) => {
        ageRepository.insertAges(response.data);
      });
      await this.getSitesChecksum().then((response) => {
        sitesChecksumRepository.insertSitesChecksum({
          checksum: response.data,
        });
      });
      await this.getSpeciesChecksum().then((response) => {
        speciesChecksumRepository.insertSpeciesChecksum({
          checksum: response.data,
        });
      });
      await this.getFinnPointsChecksum().then((response) => {
        finnPointsChecksumRepository.insertFinnPointsChecksum({
          checksum: response.data,
        });
      });
      await this.getPlumages().then((response) => {
        plumageRepository.insertPlumages(response.data);
      });
      await this.getSpecies().then((response) => {
        speciesRepository.insertSpecies(response.data);
      });
      await this.getFinnSites().then((response) => {
        finnSiteRepository.insertFinnSites(response.data);
      });
      await this.getFinnPoints().then(async (response) => {
        await finnPointRepository.clearAll();
        await finnPointRepository.insertFinnPoints(response.data);
      });
      await this.getSites().then((response) => {
        siteRepository.insertSites(response.data);
      });
      await this.getAtlasSquarePoints().then((response) => {
        atlasSquarePointRepository.insertAtlasSquarePoints(response.data);
      });
      await this.getSpecieRankings().then((response) => {
        specieRankingRepository.insertSpecieRankings(response.data);
      });
      await this.getGeneralAtlasSquareStatistics().then((response) => {
        generalAtlasSquareStatistics.insertGeneralAtlasSquareStatistics(
          response.data
        );
      });
    },
    getAges() {
      return axios.get("/public/ages");
    },
    getPlumages() {
      return axios.get("/public/plumages");
    },
    getSpecies() {
      return axios.get("/public/species");
    },
    getFinnSites() {
      return axios.get("/public/gis/finn/sites");
    },
    getSites() {
      return axios.get("/public/sites");
    },
    getFinnPoints() {
      return axios.get("/public/finn/points");
    },
    getSpeciesChecksum() {
      return axios.get("/public/species/checksum");
    },
    getSitesChecksum() {
      return axios.get("/public/sites/checksum");
    },
    getFinnPointsChecksum() {
      return axios.get("/public/finn/points/checksum");
    },
    getAtlasSquarePoints() {
      return axios.get("/spontan/atlas-squares/points");
    },
    getSpecieRankings() {
      return axios.get("/spontan/statistics/general/species");
    },
    getGeneralAtlasSquareStatistics() {
      return axios.get("/spontan/statistics/general");
    },
    getPrimaryBehaviours() {
      return axios.get("/public/behaviours/primary");
    },
    getSecondaryBehaviours() {
      return axios.get("/public/behaviours/secondary");
    },
    getDirections() {
      return axios.get("/public/behaviours/direction");
    },
    toggleShowAlert() {
      this.showAlert = !this.showAlert;
    },
    async attemptLogin() {
      let inputs = {
        username: this.username,
        password: this.password,
      };
      const response = await axios.post("login", inputs).catch((error) => {
        if (error.response) {
          // Request made and server responded
          console.log(error.response.data);
          console.log(error.response.status);
          console.log(error.response.headers);
          console.log(error.message);
          if (error.response.status) {
            // Display message for user indicating login credentials were not correct
            this.toggleShowAlert();
          }
        } else if (error.request) {
          // The request was made but no response was received
          console.log(error.request);
        } else {
          // Something happened in setting up the request that triggered an Error
          console.log("Error", error.message);
          this.toggleShowAlert();
        }
      });
      if (
        response &&
        response.data.access_token &&
        response.data.refresh_token
      ) {
        localStorage.setItem(
          "access_token",
          JSON.stringify(response.data.access_token)
        );
        localStorage.setItem(
          "refresh_token",
          JSON.stringify(response.data.refresh_token)
        );
        console.log("Received tokens from DOFbasen, now logging in..");
        this.login();
      }
    },
    async prepareAudio() {
      fetch(this.alarmSound)
        .then((res) => res.blob()) // Gets the response and returns it as a blob
        .then((blob) => {
          localforage.setItem("audio", blob);
        });
    },
    async prepareOfflineMap() {
      fetch(this.offlineMapImage)
        .then((res) => res.blob())
        .then((blob) => {
          localforage.setItem("offlineMap", blob);
        });
    },
    async login() {
      console.log("logging in");
      this.isLoading = true;
      await this.getData();
      this.prepareAudio();
      this.prepareOfflineMap();
      synchronizationService.setSynchCookie();
      this.isLoading = false;
      this.$router.push("/");
    },
  },
  computed: {
    isLoginCredentialsEmpty() {
      if (
        this.username &&
        this.username.trim() &&
        this.password &&
        this.password.trim()
      ) {
        return false;
      } else {
        return true;
      }
    },
  },
};
</script>

<style scoped>
.login-page {
  background: radial-gradient(
      circle at 15% 15%,
      rgb(255 255 255 / 14%),
      transparent 19rem
    ),
    radial-gradient(
      circle at 90% 80%,
      rgb(255 255 255 / 10%),
      transparent 22rem
    ),
    linear-gradient(135deg, #1f7aaf 0%, #258e8c 45%, #51af30 100%);
  min-height: 100vh;
  min-height: 100dvh;
  overflow-x: hidden;
}

.login-shell {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: max(1rem, env(safe-area-inset-top))
    max(0.75rem, env(safe-area-inset-right))
    max(1rem, env(safe-area-inset-bottom))
    max(0.75rem, env(safe-area-inset-left));
}

.login-card {
  background: rgb(255 255 255 / 97%);
  border: 1px solid rgb(255 255 255 / 45%);
  border-radius: 1.35rem;
  box-shadow: 0 22px 55px rgb(8 51 68 / 25%);
  max-width: 29rem;
  padding: clamp(1.1rem, 5vw, 1.7rem);
  width: 100%;
}

.login-heading {
  text-align: center;
}

.login-logo-wrap {
  align-items: center;
  background: #fff;
  border: 1px solid #e7eef0;
  border-radius: 1rem;
  box-shadow: 0 7px 18px rgb(24 67 85 / 9%);
  display: flex;
  height: 5.1rem;
  justify-content: center;
  margin: 0 auto 0.75rem;
  padding: 0.55rem;
  width: 5.1rem;
}

.login-logo-wrap img {
  height: auto;
  max-height: 100%;
  max-width: 100%;
}

.login-kicker {
  color: #3a9829;
  display: block;
  font-size: 0.67rem;
  font-weight: 800;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.login-heading h1 {
  color: #173f54;
  font-size: clamp(1.55rem, 7vw, 2rem);
  font-weight: 850;
  letter-spacing: -0.035em;
  margin: 0.12rem 0 0.25rem;
}

.login-heading p {
  color: #637780;
  font-size: 0.86rem;
  margin: 0;
}

.login-read-more {
  align-items: center;
  color: #1f7aaf;
  display: inline-flex;
  font-size: 0.77rem;
  font-weight: 750;
  gap: 0.25rem;
  margin-top: 0.35rem;
  text-decoration: none;
}

.login-read-more .material-icons {
  font-size: 0.9rem;
}

.login-install-prompt {
  margin: 0.9rem 0 0.75rem;
}

.login-install-prompt :deep(#install-app-btn),
.login-install-prompt
  :deep(#ios-webkit-install-instructions-container .btn-default) {
  background: #edf7ef;
  border: 1px solid rgb(81 175 48 / 22%);
  border-radius: 999px;
  color: #398c22;
  font-size: 0.78rem;
  font-weight: 800;
  min-height: 2.55rem;
  padding: 0.45rem 0.8rem;
}

.login-install-prompt :deep(#pwa-browser-compability-container),
.login-install-prompt :deep(#ios-webkit-install-instructions-container) {
  color: #637780;
  font-size: 0.73rem;
  line-height: 1.4;
}

.login-install-prompt :deep(.card) {
  border: 1px solid #e2ebee;
  border-radius: 0.8rem;
  box-shadow: none;
  text-align: left;
}

.login-alert {
  align-items: flex-start;
  background: #fff4e5;
  border: 1px solid rgb(255 136 0 / 25%);
  border-radius: 0.8rem;
  color: #72470c;
  display: grid;
  font-size: 0.79rem;
  gap: 0.45rem;
  grid-template-columns: auto 1fr auto;
  line-height: 1.4;
  margin-bottom: 0.75rem;
  padding: 0.65rem;
}

.login-alert > .material-icons {
  color: #df7900;
  font-size: 1.1rem;
}

.login-alert button {
  background: transparent;
  border: 0;
  color: #72470c;
  font-size: 1.25rem;
  line-height: 0.8;
  padding: 0;
}

.login-form {
  display: grid;
  gap: 0.4rem;
}

.login-form > label {
  color: #36515f;
  font-size: 0.72rem;
  font-weight: 800;
  margin: 0.2rem 0 0 0.15rem;
}

.login-input-wrap {
  align-items: center;
  background: #f5f8f9;
  border: 1px solid #d9e4e8;
  border-radius: 0.8rem;
  display: grid;
  grid-template-columns: auto 1fr;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.login-input-wrap:focus-within {
  border-color: #1f7aaf;
  box-shadow: 0 0 0 3px rgb(31 122 175 / 11%);
}

.login-input-wrap .material-icons {
  color: #70848e;
  font-size: 1.15rem;
  margin-left: 0.75rem;
}

.login-input-wrap input {
  background: transparent;
  border: 0;
  color: #203d4b;
  font-size: 0.92rem;
  min-height: 3.15rem;
  min-width: 0;
  outline: 0;
  padding: 0.65rem 0.75rem 0.65rem 0.55rem;
  width: 100%;
}

.login-input-wrap input::placeholder {
  color: #94a2a8;
}

.login-forgot-link {
  color: #1f7aaf;
  font-size: 0.74rem;
  font-weight: 750;
  justify-self: end;
  margin: 0.05rem 0 0.35rem;
  text-decoration: none;
}

.login-submit-button {
  align-items: center;
  background: linear-gradient(135deg, #155b82, #1f7aaf);
  border: 0;
  border-radius: 0.8rem;
  box-shadow: 0 7px 18px rgb(31 122 175 / 20%);
  color: #fff;
  display: flex;
  font-size: 0.92rem;
  font-weight: 800;
  justify-content: space-between;
  min-height: 3.15rem;
  padding: 0.6rem 0.85rem 0.6rem 1rem;
  transition: opacity 0.15s ease, transform 0.15s ease;
  width: 100%;
}

.login-submit-button:not(:disabled):active {
  transform: translateY(1px);
}

.login-submit-button:disabled {
  box-shadow: none;
  cursor: not-allowed;
  opacity: 0.48;
}

.login-submit-button .material-icons {
  font-size: 1.2rem;
}

@media (max-height: 720px) {
  .login-shell {
    align-items: flex-start;
  }
}
</style>
